import { computed } from "vue"
import { useAuthStore } from "@/store/authentication"
import { FEATURE_SETS } from "@/services/featureSet"
import { isEntitled } from "@/services/entitlements"
import { hasPermissionsOfAll } from "@/services/permissionService"
import { PERMISSIONS } from "@/services/permission"

/**
 * Whether every purchase price row is a supplier yet (docs/suppliers.md, "Setup and strict mode").
 *
 * A workspace switches once its supplier setup is done: until then machines and materials keep their freely
 * named prices and the Einkauf of a record is edited in its Allgemein tab, as before. After it, prices change
 * only at the supplier or through the record's Einkauf tab, and the server refuses the old way (it ignores
 * the prices of a record save). The state comes with /principal, so the SPA follows the server's view; a
 * workspace without the SUPPLIERS feature-set is never strict, whatever the state says.
 */
export const SUPPLIER_SETUP = {
  LEGACY: "LEGACY",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
}

const strict = () => useAuthStore().getSupplierSetup === SUPPLIER_SETUP.COMPLETED && isEntitled(FEATURE_SETS.SUPPLIERS)

/** Reactive: true once the workspace's prices are supplier rows. */
export const useSupplierStrict = () => computed(strict)

/** Non-reactive, for guards and plain functions. */
export const isSupplierStrict = strict

/**
 * What the setup asks (the server checks the same): it creates suppliers and assigns them to the purchase
 * prices of every machine and material, and it names them, so both catalogues' read and price rights.
 */
export const SUPPLIER_SETUP_PERMISSIONS = [
  PERMISSIONS.EDIT_SUPPLIER_GENERAL,
  PERMISSIONS.VIEW_MACHINE_GENERAL,
  PERMISSIONS.VIEW_MATERIAL_GENERAL,
  PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE,
  PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE,
]

/**
 * Who may run the setup, in any state: once done it still assigns a supplier to prices written without one
 * meanwhile (the server lists them as long as there are any).
 */
export const useCanRunSupplierSetup = () => {
  const rights = hasPermissionsOfAll(SUPPLIER_SETUP_PERMISSIONS)
  return computed(() => rights.value && isEntitled(FEATURE_SETS.SUPPLIERS) && useAuthStore().getSupplierSetup !== null)
}

/** The answers that mean this tab's idea of the supplier mode is stale (another tab finished the setup). */
export const STALE_SUPPLIER_MODE_CODES = [
  "SUPPLIER_SETUP_REQUIRED",
  "PURCHASE_PRICE_VARIANT_SUPPLIER_REQUIRED",
  "PURCHASE_PRICES_AT_SUPPLIER",
]
